/**
 * DatabaseEditFormBody — shared form body for CREATE / ALTER DATABASE.
 *
 * Engines:
 *   - mssql / postgres / mysql / oracle: full form.
 *   - sqlite: unsupported affordance with documented redirect tooltip
 *             (databases are filesystem files; use ATTACH DATABASE).
 *
 * Modes:
 *   - `create`: builds a `DatabaseDef` and dispatches `commitCreateDatabase`.
 *   - `alter`:  builds a `DatabaseChange` and dispatches `commitAlterDatabase`.
 *
 * The owning popover seeds `initialValues` from the parked statement.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice G1.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  commitAlterDatabase,
  getAlterDatabaseUnsupportedTooltip,
  isAlterDatabaseSupported,
  validateAlterDatabaseForEngine,
} from '../../edit/edits/alterDatabase';
import {
  commitCreateDatabase,
  getCreateDatabaseUnsupportedTooltip,
  isCreateDatabaseSupported,
  validateCreateDatabaseForEngine,
} from '../../edit/edits/createDatabase';
import type { VqeDdlEditContext } from '../../edit/editContext';
import {
  DEFAULT_DATABASE_FORM_VALUES,
  buildDatabaseChangeFromForm,
  buildDatabaseDefFromForm,
  type DatabaseEditMode,
  type DatabaseFormValues,
} from './databaseFormHelpers';
import { DatabaseFormFields } from './DatabaseFormFields';
import { FormActionsSlot } from './FormActionsSlot';
import { FormEngineChip } from './FormEngineChip';

export interface DatabaseEditFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly mode: DatabaseEditMode;
  readonly initialValues?: Partial<DatabaseFormValues>;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function DatabaseEditFormBody(props: DatabaseEditFormBodyProps) {
  const { editContext, mode, initialValues, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const supported =
    mode === 'create' ? isCreateDatabaseSupported(engine) : isAlterDatabaseSupported(engine);
  const unsupportedTooltip =
    mode === 'create'
      ? getCreateDatabaseUnsupportedTooltip(engine)
      : getAlterDatabaseUnsupportedTooltip(engine);

  const [values, setValues] = useState<DatabaseFormValues>({
    ...DEFAULT_DATABASE_FORM_VALUES,
    ...initialValues,
  });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!supported) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <div className="text-xs font-semibold">
          {mode === 'create' ? 'Create database' : 'Edit database'}
        </div>
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-unsupported`}
        >
          {unsupportedTooltip}
        </div>
        <div className="flex items-center justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onCancel?.()}
            data-testid={`${testId}-close`}
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  const update = <K extends keyof DatabaseFormValues>(key: K, value: DatabaseFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  async function handleSubmit() {
    setError(null);
    if (mode === 'create') {
      const database = buildDatabaseDefFromForm(engine, values);
      const validationError = validateCreateDatabaseForEngine(engine, { database });
      if (validationError !== null) {
        setError(validationError);
        return;
      }
      setPending(true);
      try {
        await commitCreateDatabase(editContext, { database });
        onCommitted?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPending(false);
      }
      return;
    }
    const args = {
      databaseName: values.name.trim(),
      ...(values.quoted ? { quoted: true } : {}),
      ...(engine === 'mysql' && values.mysqlSchema ? { mysqlSchema: true } : {}),
      change: buildDatabaseChangeFromForm(engine, values),
    };
    const validationError = validateAlterDatabaseForEngine(engine, args);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitAlterDatabase(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  const submitLabel = pending
    ? 'Saving…'
    : mode === 'create' ? 'Create database' : 'Apply changes';

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">
          {mode === 'create' ? 'Create database' : 'Edit database'}
        </div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${testId}-name`} className="text-[11px]">
          {mode === 'create' ? 'Name' : 'Existing name'}
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          className="h-7 text-xs"
          value={values.name}
          onChange={(e) => update('name', e.target.value)}
        />
      </div>

      <DatabaseFormFields
        engine={engine}
        mode={mode}
        values={values}
        testId={testId}
        update={update}
      />

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <FormActionsSlot className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {submitLabel}
        </Button>
      </FormActionsSlot>
    </div>
  );
}
